import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import ArrowUp from 'apollo-react-icons/ArrowUp';
import Plus from 'apollo-react-icons/Plus';
import Trash from 'apollo-react-icons/Trash';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ReportModelPickerModal from './ReportModelPickerModal';
import {
  addControl,
  changeControlPositionByButton,
  deleteControl,
} from '../actions/report-designer.actions';
import {
  CONTROL_TYPES_WITH_ACTIONS_UNDER_CONTROL,
  INLINE_CONTROL_TYPES,
  MOVE_BUTTON_TYPES,
  REPORT_CONTROL_TYPE,
} from '../constants/report-designer.constants';
import {
  getControlProperty,
  getParentChildrenControlTypes,
} from '../selectors/report-designer.selectors';
import { getIsDeleteControlDisabled, getIsMoveBtnDisabled } from '../utils/report-designer.utils';

const useStyles = makeStyles({
  root: { display: 'flex', alignItems: 'center' },
  control: { flexGrow: 1, width: '100%' },
  controlSelected: { width: 'calc(100% - 160px)' },
  bottomActions: {
    position: 'absolute',
    top: '100%',
    right: 0,
    zIndex: 1001,
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'white',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: 2,
  },
  rotate: { transform: 'rotate(-90deg)' },
  floatingButtons: {
    position: 'absolute',
    display: 'flex',
    flexGrow: 'nowrap',
    backgroundColor: 'white',
    borderRadius: 6,
    zIndex: 1001,
    boxShadow: '0px 1px 10px 0 #d9d9d9',
    right: 0,
    bottom: 'calc(100% + 4px)',
    padding: 2,
  },
  disabled: {
    opacity: 0.4,
  },
});

const ControlActions = ({ children, id, controlType, isActionsVisible }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isReportModelOpen, setReportModelOpen] = useState(false);

  const parentId = useSelector((state) => getControlProperty(state, { id, prop: 'parentId' }));
  const parentChildrenControlTypes = useSelector((state) =>
    getParentChildrenControlTypes(state, { parentId })
  );

  const shouldShowActionsUnderControl = CONTROL_TYPES_WITH_ACTIONS_UNDER_CONTROL.includes(
    controlType
  );
  const hasAddButton = controlType === REPORT_CONTROL_TYPE.CROSS_TAB_COLUMN;

  const reportModelFieldSelect = (mappingValues = {}) => {
    if (!_.isEmpty(mappingValues)) {
      setReportModelOpen(false);
      dispatch(addControl({ parentId, controlType, mappingValues }));
    }
  };

  const isUpButtonDisabled = getIsMoveBtnDisabled(
    id,
    controlType,
    parentChildrenControlTypes,
    MOVE_BUTTON_TYPES.UP
  );
  const isDownButtonDisabled = getIsMoveBtnDisabled(
    id,
    controlType,
    parentChildrenControlTypes,
    MOVE_BUTTON_TYPES.DOWN
  );
  const isDeleteButtonDisabled = getIsDeleteControlDisabled(
    controlType,
    id,
    parentChildrenControlTypes
  );

  return (
    <div className={classes.root}>
      <div
        className={classNames({
          [classes.control]: true,
          [classes.controlSelected]: isActionsVisible && !shouldShowActionsUnderControl,
        })}
      >
        {children}
      </div>
      {isActionsVisible && (
        <div
          className={classNames({
            [classes.bottomActions]: isActionsVisible && shouldShowActionsUnderControl,
            [classes.floatingButtons]: isActionsVisible && !shouldShowActionsUnderControl,
          })}
        >
          <IconButton
            className={classNames({
              [classes.rotate]: INLINE_CONTROL_TYPES.includes(controlType),
              [classes.disabled]: isUpButtonDisabled,
            })}
            size="small"
            disabled={isUpButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(changeControlPositionByButton(id, MOVE_BUTTON_TYPES.UP));
            }}
          >
            <ArrowUp color="primary" />
          </IconButton>
          <IconButton
            className={classNames({
              [classes.rotate]: INLINE_CONTROL_TYPES.includes(controlType),
              [classes.disabled]: isDownButtonDisabled,
            })}
            size="small"
            disabled={isDownButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(changeControlPositionByButton(id, MOVE_BUTTON_TYPES.DOWN));
            }}
          >
            <ArrowDown color="primary" />
          </IconButton>
          {hasAddButton && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setReportModelOpen(true);
              }}
            >
              <Plus color="primary" />
            </IconButton>
          )}
          <IconButton
            size="small"
            className={classNames({ [classes.disabled]: isDeleteButtonDisabled })}
            disabled={isDeleteButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(deleteControl(id));
            }}
          >
            <Trash color="primary" />
          </IconButton>
        </div>
      )}
      <ReportModelPickerModal
        open={isReportModelOpen}
        onClose={() => setReportModelOpen(false)}
        onChange={reportModelFieldSelect}
      />
    </div>
  );
};

export default ControlActions;
